import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, FlaskConical, Mail, User as UserIcon, BookOpen,
  Package, Truck, ExternalLink, ArrowRight, Search,
  Home as HomeIcon, MapPin, FileText, Bell, Trash2, RefreshCcw,
  Star, ShieldCheck, Download, Plus, Pencil, Loader2, CheckCircle2, AlertTriangle,
  X as XIcon, Save, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { statusPillClass, statusLabel } from "../data/orderStatus";
import SecurityPanel from "./account/SecurityPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { key: "profile",     label: "Profile",           icon: UserIcon },
  { key: "orders",      label: "Orders",            icon: Package },
  { key: "documents",   label: "My Documents",      icon: FileText },
  { key: "addresses",   label: "Addresses",         icon: MapPin },
  { key: "preferences", label: "Email Preferences", icon: Bell },
  { key: "security",    label: "Password",          icon: ShieldCheck },
  { key: "affiliate",   label: "Affiliate",         icon: Star },
  { key: "danger",      label: "Delete Account",    icon: Trash2 },
];

export default function Account() {
  const { user, token, logout, refreshUser } = useAuth();
  const [tab, setTab] = useState("profile");

  if (!user) return null; // ProtectedRoute will redirect

  const navItems = NAV_ITEMS.filter((n) => n.key !== "affiliate" || user.is_affiliate);

  return (
    <main
      data-testid="account-page"
      className="min-h-[calc(100vh-7rem)] bg-cream-200/40 py-14 md:py-20"
    >
      <div className="container-x max-w-6xl">
        <p className="overline mb-2">My Account</p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 leading-tight">
              Hello, {user.first_name}.
            </h1>
            <p className="mt-3 text-sm text-charcoal-400 max-w-xl">
              Manage your researcher profile, past orders, and documents.
            </p>
          </div>
          {user.is_affiliate && (
            <span
              data-testid="account-affiliate-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#8B2252]/[0.08] border border-[#8B2252]/30 text-[#8B2252] text-xs font-semibold"
            >
              <Star className="w-3.5 h-3.5" strokeWidth={2} />
              Affiliate Partner
            </span>
          )}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8 md:gap-10">
          {/* Left nav */}
          <nav
            data-testid="account-nav"
            className="surface-card p-3 h-max sticky top-8"
          >
            {navItems.map((item) => (
              <NavButton
                key={item.key}
                active={tab === item.key}
                onClick={() => setTab(item.key)}
                icon={item.icon}
                label={item.label}
                danger={item.key === "danger"}
                testId={`account-nav-${item.key}`}
              />
            ))}
            <div className="mt-2 pt-2 border-t border-charcoal-100">
              <NavButton
                onClick={logout}
                icon={LogOut}
                label="Sign out"
                testId="account-logout-button"
              />
            </div>
          </nav>

          {/* Content */}
          <div className="min-w-0">
            {tab === "profile"     && <ProfilePanel     user={user} token={token} refreshUser={refreshUser} />}
            {tab === "orders"      && <OrdersPanel      token={token} />}
            {tab === "documents"   && <DocumentsPanel   token={token} />}
            {tab === "addresses"   && <AddressesPanel   token={token} />}
            {tab === "preferences" && <PreferencesPanel user={user} token={token} refreshUser={refreshUser} />}
            {tab === "security"    && <SecurityPanel token={token} />}
            {tab === "affiliate"   && user.is_affiliate && <AffiliatePanel user={user} token={token} />}
            {tab === "danger"      && <DangerZonePanel   token={token} logout={logout} />}
          </div>
        </div>
      </div>
    </main>
  );
}

// ------------------------------------------------------------
// Left nav button
// ------------------------------------------------------------
const NavButton = ({ active, onClick, icon: Icon, label, danger, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-left transition-colors ${
      active
        ? "bg-[#8B2252] text-white font-medium"
        : danger
        ? "text-red-600 hover:bg-red-50"
        : "text-charcoal-600 hover:bg-cream-200"
    }`}
  >
    <Icon className="w-4 h-4" strokeWidth={2} />
    {label}
  </button>
);

// ------------------------------------------------------------
// PROFILE PANEL (Feature 4 — edit name; email is read-only)
// ------------------------------------------------------------
const ProfilePanel = ({ user, token, refreshUser }) => {
  const [firstName, setFirstName] = useState(user.first_name || "");
  const [lastName, setLastName] = useState(user.last_name || "");
  const [saving, setSaving] = useState(false);

  const dirty = firstName.trim() !== (user.first_name || "")
    || lastName.trim() !== (user.last_name || "");

  const save = async () => {
    if (!firstName.trim() || !lastName.trim()) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/auth/me`,
        { first_name: firstName.trim(), last_name: lastName.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await refreshUser();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <WelcomeOfferCard token={token} />
      <div className="surface-card p-8 md:p-10" data-testid="profile-panel">
      <p className="overline mb-5">Profile</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="First name" required>
          <input
            data-testid="profile-first-name"
            className="input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={80}
          />
        </Field>
        <Field label="Last name" required>
          <input
            data-testid="profile-last-name"
            className="input"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={80}
          />
        </Field>
        <Field label="Email (read-only)">
          <input
            data-testid="profile-email"
            className="input bg-cream-200/60 text-charcoal-400 cursor-not-allowed"
            value={user.email}
            readOnly
            disabled
          />
        </Field>
        <Field label="Research field">
          <input
            className="input bg-cream-200/60 text-charcoal-500"
            value={user.research_field || "—"}
            readOnly
            disabled
          />
        </Field>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="profile-save-btn"
          onClick={save}
          disabled={!dirty || saving || !firstName.trim() || !lastName.trim()}
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
            : <><Save className="w-4 h-4 mr-2" strokeWidth={2} />Save changes</>}
        </button>
        {!dirty && (
          <span className="text-xs text-charcoal-400">
            Change your name to enable Save.
          </span>
        )}
      </div>

      <div className="mt-8 pt-6 border-t border-charcoal-100 text-xs text-charcoal-400">
        Account created {new Date(user.created_at).toLocaleDateString(undefined, {
          year: "numeric", month: "long", day: "numeric",
        })}
      </div>
    </div>
    </>
  );
};

// ------------------------------------------------------------
// WELCOME OFFER CARD — shows FIRST10 while the customer is still
// eligible (has not yet redeemed the code on any prior order).
// Auto-hides once GET /api/discounts/welcome-offer returns eligible=false.
// ------------------------------------------------------------
const WelcomeOfferCard = ({ token }) => {
  const [offer, setOffer] = useState(null);   // { eligible, code, percent }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/discounts/welcome-offer`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setOffer(data);
      } catch {
        // Silent — card just stays hidden if the endpoint is unreachable.
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const copyCode = async () => {
    if (!offer?.code) return;
    try {
      await navigator.clipboard?.writeText(offer.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  if (!offer || !offer.eligible) return null;

  return (
    <div
      data-testid="account-welcome-offer-card"
      className="mb-6 rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4"
      style={{
        backgroundColor: "#F5EAE6",
        borderColor: "rgba(139, 34, 82, 0.25)",
      }}
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        style={{
          backgroundColor: "rgba(139, 34, 82, 0.12)",
          color: "#8B2252",
        }}
      >
        <Sparkles className="w-4 h-4" strokeWidth={2} />
      </div>

      <div className="flex-1 min-w-0">
        <p
          className="text-[10px] font-semibold uppercase tracking-[0.2em]"
          style={{ color: "#8B2252" }}
        >
          Welcome offer
        </p>
        <p className="mt-1 text-sm text-charcoal-600 leading-relaxed">
          First order discount:{" "}
          <span
            data-testid="account-welcome-offer-code"
            className="inline-block font-mono font-bold tracking-[0.14em] px-2 py-0.5 rounded text-sm"
            style={{
              color: "#8B2252",
              backgroundColor: "rgba(139, 34, 82, 0.1)",
            }}
          >
            {offer.code}
          </span>{" "}
          — <strong style={{ color: "#8B2252" }}>{offer.percent}% off</strong>
        </p>
      </div>

      <button
        type="button"
        onClick={copyCode}
        data-testid="account-welcome-offer-copy"
        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-xs font-semibold text-white transition-colors shrink-0"
        style={{ backgroundColor: copied ? "#3f7c59" : "#8B2252" }}
      >
        {copied ? (
          <>
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
            Copied
          </>
        ) : (
          <>
            <FileText className="w-3.5 h-3.5" strokeWidth={2} />
            Copy code
          </>
        )}
      </button>
    </div>
  );
};

const Field = ({ label, required, hint, children }) => (
  <label className="block">
    <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
      {label}
      {required && <span className="ml-1 text-[#8B2252]">*</span>}
    </span>
    {children}
    {hint && <span className="block text-xs text-charcoal-300 mt-1.5">{hint}</span>}
  </label>
);

// ------------------------------------------------------------
// ORDERS PANEL (existing + Feature 1: Reorder button)
// ------------------------------------------------------------
const ORDER_FILTERS = [
  { key: "all",       label: "All",                    match: null },
  { key: "pending",   label: "Pending Payment",        match: "Pending Payment - Zelle" },
  { key: "confirmed", label: "Payment Confirmed",      match: "Payment Confirmed" },
  { key: "shipped",   label: "Shipped / Fulfilled",    match: "Shipped / Fulfilled" },
];

const OrdersPanel = ({ token }) => {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setError("");
    axios
      .get(`${API}/orders/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!cancelled) setOrders(data || []); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load orders");
      });
    return () => { cancelled = true; };
  }, [token]);

  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    const filterMatch = ORDER_FILTERS.find((f) => f.key === activeFilter)?.match;
    const needle = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (filterMatch && o.status !== filterMatch) return false;
      if (needle && !o.order_number.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [orders, activeFilter, search]);

  if (orders === null && !error) {
    return <LoadingCard testId="orders-loading">Loading orders…</LoadingCard>;
  }
  if (error) {
    return <div data-testid="orders-error" className="surface-card p-8 text-sm text-red-700">{error}</div>;
  }
  if (orders.length === 0) {
    return (
      <div data-testid="orders-empty" className="surface-card p-10 text-center">
        <Package className="w-7 h-7 text-charcoal-300 mx-auto mb-3" strokeWidth={1.5} />
        <p className="font-display text-lg text-charcoal-600 mb-1">No orders yet.</p>
        <p className="text-sm text-charcoal-400 mb-6">
          Once you place your first order it will show up here.
        </p>
        <Link to="/products" className="btn-primary">
          Browse compounds <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="orders-panel" className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-2">
        <div data-testid="orders-filter-pills" className="flex flex-wrap gap-2">
          {ORDER_FILTERS.map((f) => {
            const active = f.key === activeFilter;
            const count = f.match
              ? orders.filter((o) => o.status === f.match).length
              : orders.length;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setActiveFilter(f.key)}
                data-testid={`orders-filter-${f.key}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                  active
                    ? "bg-[#8B2252] text-white"
                    : "bg-white text-charcoal-500 border border-charcoal-100 hover:bg-cream-200"
                }`}
              >
                {f.label}
                <span className={`ml-1.5 ${active ? "opacity-80" : "text-charcoal-400"}`}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative md:ml-auto md:max-w-xs md:flex-1">
          <Search className="w-4 h-4 text-charcoal-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={2} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order number…"
            data-testid="orders-search-input"
            className="input pl-10 text-sm"
          />
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div data-testid="orders-filtered-empty" className="surface-card p-10 text-center">
          <Search className="w-7 h-7 text-charcoal-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-display text-lg text-charcoal-600 mb-1">No orders match your filter.</p>
        </div>
      ) : (
        filteredOrders.map((o) => <OrderRow key={o.order_number} order={o} token={token} />)
      )}
    </div>
  );
};

const OrderRow = ({ order, token }) => {
  const { addItem, setOpen: openCart } = useCart();
  const navigate = useNavigate();
  const [reordering, setReordering] = useState(false);
  const dateStr = new Date(order.created_at).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const reorder = async () => {
    setReordering(true);
    try {
      const { data } = await axios.post(
        `${API}/orders/${order.order_number}/reorder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const added = data.added || [];
      const unavailable = data.unavailable || [];
      added.forEach((it) => {
        addItem(it.product_id, it.variant_id, it.quantity);
      });
      if (added.length > 0 && unavailable.length === 0) {
        toast.success(`${added.length} item${added.length === 1 ? "" : "s"} added to your cart.`);
      } else if (added.length > 0) {
        toast.success(
          `${added.length} of ${added.length + unavailable.length} items added. ` +
          `${unavailable.length} no longer available.`,
        );
      } else {
        toast.error("None of these items are currently available.");
      }
      if (added.length > 0) {
        openCart(true);
        navigate("/cart");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reorder");
    } finally {
      setReordering(false);
    }
  };

  return (
    <article
      data-testid={`customer-order-${order.order_number}`}
      className="surface-card p-6 md:p-7"
    >
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <Link
            to={`/order/${order.order_number}`}
            className="font-display font-bold text-xl text-[#8B2252] hover:underline"
          >
            {order.order_number}
          </Link>
          <p className="text-xs text-charcoal-400 mt-0.5">{dateStr}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span
            data-testid={`customer-status-pill-${order.order_number}`}
            className={`text-[10px] font-medium px-3 py-1.5 rounded-full uppercase tracking-wider ${statusPillClass(order.status)}`}
          >
            {statusLabel(order.status)}
          </span>
          <span className="font-display font-bold text-lg text-charcoal-600">
            ${order.total.toFixed(2)}
          </span>
        </div>
      </header>

      <ul className="space-y-1 text-sm text-charcoal-500 mb-4">
        {order.items.map((it) => (
          <li key={`${it.product_id}-${it.variant_id}`} className="flex justify-between gap-3">
            <span>{it.product_name} · {it.variant_label} × {it.quantity}</span>
            <span className="text-charcoal-400">${it.line_total.toFixed(2)}</span>
          </li>
        ))}
      </ul>

      {/* Admin edit audit trail — surfaced so customers can self-serve
          verify any corrections we made to their order. Kept subtle: soft
          neutral panel, gentle "info" tone, no alarming colors. Only
          rendered when admin_history has something to show. */}
      {(order.admin_history || []).length > 0 && (
        <div
          data-testid={`order-history-${order.order_number}`}
          className="mt-2 pt-4 border-t border-charcoal-100/60 mb-3"
        >
          <p className="overline text-charcoal-500 mb-2">Order updates</p>
          <ul className="space-y-1.5 text-xs text-charcoal-500">
            {order.admin_history.map((h, i) => {
              const when = h.at
                ? new Date(h.at).toLocaleString(undefined, {
                    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                  })
                : "";
              const label = h.action === "line_items_edited"
                ? "Line items corrected by our team"
                : h.action === "voided" || h.action === "refunded"
                ? "Refund issued"
                : (h.action || "Updated").replace(/_/g, " ");
              const delta = Math.abs((h.new_total || 0) - (h.prev_total || 0));
              return (
                <li
                  key={i}
                  data-testid={`order-history-${order.order_number}-${i}`}
                  className="flex items-start gap-2"
                >
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-charcoal-300 shrink-0" />
                  <span>
                    {label}
                    {when && <span className="text-charcoal-400"> · {when}</span>}
                    {delta > 0 && (
                      <span className="text-charcoal-400">
                        {" "}· total went from ${Number(h.prev_total).toFixed(2)} to ${Number(h.new_total).toFixed(2)}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {order.status === "Shipped / Fulfilled" && order.carrier && order.tracking_number && (
        <div
          data-testid={`customer-tracking-${order.order_number}`}
          className="mt-2 pt-4 border-t border-charcoal-100/60 flex items-start gap-3 mb-3"
        >
          <Truck className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" strokeWidth={2} />
          <div className="text-sm">
            <p className="text-charcoal-600 font-medium">
              {order.carrier} ·{" "}
              <span className="font-display font-bold select-all">{order.tracking_number}</span>
            </p>
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`tracking-link-${order.order_number}`}
                className="inline-flex items-center text-[#8B2252] hover:underline mt-0.5"
              >
                Track shipment <ExternalLink className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
              </a>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-charcoal-100/60 flex flex-wrap gap-3">
        <button
          type="button"
          data-testid={`reorder-btn-${order.order_number}`}
          onClick={reorder}
          disabled={reordering}
          className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {reordering
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding…</>
            : <><RefreshCcw className="w-4 h-4 mr-2" strokeWidth={2} />Reorder</>}
        </button>
        <Link
          to={`/order/${order.order_number}`}
          className="text-sm inline-flex items-center gap-1.5 text-charcoal-500 hover:text-[#8B2252]"
        >
          View details <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </article>
  );
};

// ------------------------------------------------------------
// DOCUMENTS PANEL (Feature 2 — COA library)
// ------------------------------------------------------------
const DocumentsPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setError("");
    axios
      .get(`${API}/me/coas`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!cancelled) setRows(data || []); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load documents");
      });
    return () => { cancelled = true; };
  }, [token]);

  if (rows === null && !error) return <LoadingCard testId="documents-loading">Loading documents…</LoadingCard>;
  if (error) return <div data-testid="documents-error" className="surface-card p-8 text-sm text-red-700">{error}</div>;

  return (
    <div className="surface-card p-8 md:p-10" data-testid="documents-panel">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <p className="overline mb-2">My Documents</p>
          <h2 className="font-display text-2xl font-bold text-charcoal-700">
            Certificate of Analysis
          </h2>
        </div>
        <ShieldCheck className="w-6 h-6 text-sage-600" strokeWidth={1.8} />
      </div>
      <p className="text-sm text-charcoal-400 mb-6">
        COA PDFs for every compound you&apos;ve purchased. New COAs added as batches ship.
      </p>

      {rows.length === 0 ? (
        <div data-testid="documents-empty" className="text-center py-10">
          <FileText className="w-6 h-6 text-charcoal-300 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-charcoal-500">
          Once you place an order, COA documents for your purchases will appear here.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-charcoal-100">
          {rows.map((row) => <DocumentRow key={row.product_id} row={row} />)}
        </div>
      )}
    </div>
  );
};

const DocumentRow = ({ row }) => {
  const dateStr = row.order_date
    ? new Date(row.order_date).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      })
    : "—";
  return (
    <div
      data-testid={`document-row-${row.product_id}`}
      className="py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-charcoal-700">
          {row.product_name}
        </div>
        <div className="text-xs text-charcoal-400 mt-0.5">
          Purchased {dateStr}
          {row.order_number && (
            <>
              {" · "}
              <Link
                to={`/order/${row.order_number}`}
                className="text-charcoal-500 hover:text-[#8B2252]"
              >
                {row.order_number}
              </Link>
            </>
          )}
        </div>
      </div>
      {row.coa_available ? (
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}${row.download_url}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`document-download-${row.product_id}`}
          className="btn-secondary text-sm shrink-0"
        >
          <Download className="w-4 h-4 mr-1.5" strokeWidth={2} />
          Download PDF
        </a>
      ) : (
        <span
          data-testid={`document-pending-${row.product_id}`}
          className="text-xs text-charcoal-400 italic shrink-0"
        >
          COA in preparation
        </span>
      )}
    </div>
  );
};

// ------------------------------------------------------------
// ADDRESSES PANEL (Feature 3 — saved addresses CRUD)
// ------------------------------------------------------------
const EMPTY_ADDR = {
  label: "", first_name: "", last_name: "",
  address1: "", address2: "", city: "", state: "",
  zip_code: "", phone: "",
};

const AddressesPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // address doc or null

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/me/addresses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load addresses");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const remove = async (addr) => {
    if (!window.confirm(`Delete this address (${addr.label || addr.address1})?`)) return;
    try {
      await axios.delete(`${API}/me/addresses/${addr.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Address deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const setDefault = async (addr) => {
    try {
      await axios.post(`${API}/me/addresses/${addr.id}/default`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Default address updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  if (rows === null && !error) return <LoadingCard testId="addresses-loading">Loading addresses…</LoadingCard>;

  return (
    <div className="surface-card p-8 md:p-10" data-testid="addresses-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline mb-2">Address Book</p>
          <h2 className="font-display text-2xl font-bold text-charcoal-700">
            Saved shipping addresses
          </h2>
        </div>
        <button
          type="button"
          data-testid="address-add-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="btn-primary text-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} />
          Add address
        </button>
      </div>
      {error && (
        <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm">
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <div data-testid="addresses-empty" className="text-center py-10">
          <HomeIcon className="w-6 h-6 text-charcoal-300 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-charcoal-500 mb-3">
            No saved addresses yet. Save one and it will auto-fill at checkout.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((addr) => (
            <AddressCard
              key={addr.id} addr={addr}
              onEdit={() => { setEditing(addr); setShowForm(true); }}
              onDelete={() => remove(addr)}
              onSetDefault={() => setDefault(addr)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <AddressFormModal
          token={token}
          initial={editing || EMPTY_ADDR}
          isEdit={!!editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
};

const AddressCard = ({ addr, onEdit, onDelete, onSetDefault }) => (
  <div
    data-testid={`address-card-${addr.id}`}
    className={`rounded-2xl border p-5 ${
      addr.is_default
        ? "border-[#8B2252]/40 bg-[#8B2252]/[0.03]"
        : "border-charcoal-100 bg-white"
    }`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-semibold text-charcoal-700">
            {addr.label || `${addr.first_name} ${addr.last_name}`.trim() || "Address"}
          </span>
          {addr.is_default && (
            <span
              data-testid={`address-default-badge-${addr.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8B2252] text-white text-[10px] font-semibold"
            >
              <CheckCircle2 className="w-2.5 h-2.5" />
              Default
            </span>
          )}
        </div>
        <div className="text-sm text-charcoal-500">
          <div>{addr.first_name} {addr.last_name}</div>
          <div>{addr.address1}{addr.address2 ? `, ${addr.address2}` : ""}</div>
          <div>{addr.city}, {addr.state} {addr.zip_code}</div>
          <div className="text-charcoal-400 text-xs mt-0.5">{addr.phone}</div>
        </div>
      </div>
      <div className="flex flex-col gap-2 items-end">
        {!addr.is_default && (
          <button
            type="button"
            data-testid={`address-set-default-${addr.id}`}
            onClick={onSetDefault}
            className="text-xs text-charcoal-500 hover:text-[#8B2252]"
          >
            Set as default
          </button>
        )}
        <button
          type="button"
          data-testid={`address-edit-${addr.id}`}
          onClick={onEdit}
          className="text-xs inline-flex items-center gap-1 text-charcoal-600 hover:text-[#8B2252]"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button
          type="button"
          data-testid={`address-delete-${addr.id}`}
          onClick={onDelete}
          className="text-xs inline-flex items-center gap-1 text-red-500 hover:text-red-700"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  </div>
);

const AddressFormModal = ({ token, initial, isEdit, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const required = ["first_name", "last_name", "address1", "city", "state", "zip_code", "phone"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Missing: ${k.replace("_", " ")}`);
        return;
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { label, first_name, last_name, address1, address2, city, state, zip_code, phone } = form;
        await axios.patch(
          `${API}/me/addresses/${initial.id}`,
          { label, first_name, last_name, address1, address2, city, state, zip_code, phone },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Address updated");
      } else {
        await axios.post(`${API}/me/addresses`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Address saved");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} testId="address-form-modal">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-2xl font-bold text-charcoal-700">
          {isEdit ? "Edit address" : "Add address"}
        </h3>
        <button onClick={onClose} className="text-charcoal-400 hover:text-charcoal-700" data-testid="address-form-close">
          <XIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Label (optional)">
          <input className="input" data-testid="address-input-label" value={form.label || ""} onChange={(e) => set("label", e.target.value)} placeholder="Home, Lab, etc." maxLength={60} />
        </Field>
        <div />
        <Field label="First name" required>
          <input className="input" data-testid="address-input-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
        </Field>
        <Field label="Last name" required>
          <input className="input" data-testid="address-input-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
        </Field>
        <Field label="Address line 1" required>
          <input className="input" data-testid="address-input-address1" value={form.address1} onChange={(e) => set("address1", e.target.value)} />
        </Field>
        <Field label="Address line 2 (optional)">
          <input className="input" data-testid="address-input-address2" value={form.address2 || ""} onChange={(e) => set("address2", e.target.value)} />
        </Field>
        <Field label="City" required>
          <input className="input" data-testid="address-input-city" value={form.city} onChange={(e) => set("city", e.target.value)} />
        </Field>
        <Field label="State" required>
          <input className="input" data-testid="address-input-state" value={form.state} onChange={(e) => set("state", e.target.value)} maxLength={40} />
        </Field>
        <Field label="ZIP code" required>
          <input className="input" data-testid="address-input-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} />
        </Field>
        <Field label="Phone" required>
          <input className="input" data-testid="address-input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </Field>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-charcoal-600">
        <input
          type="checkbox"
          data-testid="address-input-default"
          checked={!!form.is_default}
          onChange={(e) => set("is_default", e.target.checked)}
          className="accent-[#8B2252]"
        />
        Set as my default shipping address
      </label>
      <div className="mt-6 flex gap-3">
        <button type="button" className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="btn-primary flex-1"
          onClick={submit}
          disabled={saving}
          data-testid="address-form-submit"
        >
          {saving
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
            : <><Save className="w-4 h-4 mr-2" strokeWidth={2} />{isEdit ? "Save changes" : "Save address"}</>}
        </button>
      </div>
    </Modal>
  );
};

// ------------------------------------------------------------
// PREFERENCES PANEL (Feature 5 — marketing opt-in toggle)
// ------------------------------------------------------------
const PreferencesPanel = ({ user, token, refreshUser }) => {
  const [saving, setSaving] = useState(false);
  const [optIn, setOptIn] = useState(!!user.marketing_opt_in);

  useEffect(() => { setOptIn(!!user.marketing_opt_in); }, [user.marketing_opt_in]);

  const toggle = async (nextValue) => {
    setSaving(true);
    setOptIn(nextValue);
    try {
      await axios.patch(
        `${API}/auth/me`,
        { marketing_opt_in: nextValue },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await refreshUser();
      toast.success(nextValue
        ? "You're subscribed to LuviaRx emails."
        : "You've unsubscribed from marketing emails.");
    } catch (e) {
      setOptIn(!nextValue);
      toast.error(e?.response?.data?.detail || "Could not update preference");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card p-8 md:p-10" data-testid="preferences-panel">
      <p className="overline mb-2">Email Preferences</p>
      <h2 className="font-display text-2xl font-bold text-charcoal-700 mb-6">
        Marketing communications
      </h2>

      <div className="flex items-start gap-4 p-5 rounded-2xl bg-cream-100/60 border border-charcoal-100">
        <Bell className="w-5 h-5 text-[#8B2252] mt-0.5 flex-shrink-0" strokeWidth={2} />
        <div className="flex-1">
          <div className="font-semibold text-charcoal-700 mb-1">
            Newsletters & product updates
          </div>
          <p className="text-sm text-charcoal-500 mb-3">
            Occasional emails about new peptide batches, restocks, and members-only offers.
            Order confirmations and shipping updates are transactional and always sent.
          </p>
          <button
            type="button"
            role="switch"
            aria-checked={optIn}
            data-testid="preferences-marketing-toggle"
            onClick={() => toggle(!optIn)}
            disabled={saving}
            className={`relative inline-flex items-center gap-3 px-1 py-1 rounded-full transition-colors ${
              optIn ? "bg-[#8B2252]" : "bg-charcoal-200"
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <span className={`w-11 h-6 relative inline-block`}>
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  optIn ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </span>
            <span className={`text-xs font-semibold pr-3 ${optIn ? "text-white" : "text-charcoal-500"}`}>
              {optIn ? "SUBSCRIBED" : "UNSUBSCRIBED"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// AFFILIATE PANEL (Feature 6)
// ------------------------------------------------------------
const AffiliatePanel = ({ user, token }) => {
  const personalPct = Number(user.affiliate_personal_discount_pct) || 0;
  const referralCode = user.referral_code || "";
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/me/affiliate/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch(() => { if (!cancelled) setStats({ referral_count: 0, total_sales: 0, unpaid_commission: 0, paid_commission: 0 }); });
    return () => { cancelled = true; };
  }, [token]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  return (
    <div className="surface-card p-8 md:p-10" data-testid="affiliate-panel">
      <div className="flex items-center gap-3 mb-2">
        <Star className="w-5 h-5 text-[#8B2252]" strokeWidth={2} />
        <p className="overline">Affiliate Partner</p>
      </div>
      <h2 className="font-display text-2xl font-bold text-charcoal-700 mb-1">
        Welcome to the LuviaRx affiliate program.
      </h2>
      <p className="text-sm text-charcoal-500 mb-6">
        Thank you for partnering with us on the science.
      </p>

      {/* Personal discount block */}
      {personalPct > 0 && (
        <div className="rounded-2xl bg-[#8B2252]/[0.03] border border-[#8B2252]/20 p-5 mb-4">
          <div className="text-xs uppercase tracking-[0.18em] text-charcoal-400 font-semibold mb-1">
            Your personal discount
          </div>
          <div
            data-testid="affiliate-personal-pct"
            className="font-display text-3xl font-bold text-[#8B2252] tracking-wide"
          >
            {personalPct}% off
          </div>
          <div className="mt-1 text-sm text-charcoal-500">
            Applied automatically to your own orders at checkout — no code needed.
          </div>
        </div>
      )}

      {/* Referral code block */}
      {referralCode && (
        <div className="rounded-2xl bg-cream-100/60 border border-charcoal-100 p-5 mb-4">
          <div className="text-xs uppercase tracking-[0.18em] text-charcoal-400 font-semibold mb-1">
            Share your referral code
          </div>
          <div className="flex items-center gap-3 mt-2">
            <div
              data-testid="affiliate-referral-code"
              className="font-display text-2xl font-bold text-charcoal-700 tracking-wide select-all font-mono"
            >
              {referralCode}
            </div>
            <button
              type="button"
              onClick={copy}
              data-testid="affiliate-copy-code"
              className="btn-secondary text-xs"
            >
              {copied ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-sage-600" /> Copied</> : "Copy code"}
            </button>
          </div>
          <div className="mt-3 text-xs text-charcoal-500">
            Share this code with anyone. They save at checkout; you earn commission on every order they place.
          </div>
        </div>
      )}

      {/* Stats block */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard label="Referred orders" value={stats?.referral_count ?? "—"} testId="affiliate-stat-count" />
        <StatCard label="Total sales" value={`$${Number(stats?.total_sales || 0).toFixed(2)}`} testId="affiliate-stat-sales" />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, testId, accent }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl p-4 border ${accent ? "bg-[#8B2252]/[0.03] border-[#8B2252]/20" : "bg-white border-charcoal-100"}`}
  >
    <div className="text-xs uppercase tracking-[0.14em] text-charcoal-400 mb-1">{label}</div>
    <div className={`font-display text-xl font-bold ${accent ? "text-[#8B2252]" : "text-charcoal-700"}`}>
      {value}
    </div>
  </div>
);

// ------------------------------------------------------------
// DANGER ZONE (Feature 7 — delete account)
// ------------------------------------------------------------
const DangerZonePanel = ({ token, logout }) => {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Your account has been deleted.");
      logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account");
      setDeleting(false);
    }
  };

  return (
    <div className="surface-card p-8 md:p-10 border-red-100" data-testid="danger-panel">
      <p className="overline mb-2 text-red-700">Danger zone</p>
      <h2 className="font-display text-2xl font-bold text-charcoal-700 mb-3">
        Delete my account
      </h2>
      <div className="rounded-2xl bg-red-50 border border-red-100 p-4 mb-5 text-sm text-red-800">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2} />
          <div>
            <p className="font-semibold mb-1">This action is permanent.</p>
            <p className="text-red-700/90 leading-relaxed">
              Your account will be deactivated and you&apos;ll be logged out. Your order history is preserved for compliance purposes, but you will no longer be able to log in or receive emails from us. You&apos;ll need to register again if you want to use LuviaRx in the future.
            </p>
          </div>
        </div>
      </div>
      <button
        type="button"
        data-testid="danger-delete-btn"
        onClick={() => setShowConfirm(true)}
        className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
      >
        <Trash2 className="w-4 h-4" strokeWidth={2} />
        Delete my account
      </button>

      {showConfirm && (
        <Modal onClose={() => setShowConfirm(false)} testId="danger-confirm-modal" narrow>
          <h3 className="font-display text-2xl font-bold text-charcoal-700 mb-3">
            Are you sure?
          </h3>
          <p className="text-sm text-charcoal-500 mb-4">
            Type <strong className="text-charcoal-700 font-mono">DELETE</strong> below to confirm.
          </p>
          <input
            type="text"
            data-testid="danger-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="input mb-4"
            placeholder="DELETE"
          />
          <div className="flex gap-3">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={() => { setShowConfirm(false); setConfirmText(""); }}
              data-testid="danger-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="danger-confirm-btn"
              onClick={doDelete}
              disabled={confirmText.trim() !== "DELETE" || deleting}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {deleting
                ? <><Loader2 className="w-4 h-4 animate-spin" />Deleting…</>
                : <><Trash2 className="w-4 h-4" strokeWidth={2} />Yes, delete my account</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ------------------------------------------------------------
// Reusables
// ------------------------------------------------------------
const LoadingCard = ({ testId, children }) => (
  <div data-testid={testId} className="surface-card p-10 text-center text-charcoal-400 text-sm">
    {children}
  </div>
);

const Modal = ({ onClose, testId, narrow, children }) => (
  <div
    data-testid={testId}
    className="fixed inset-0 z-50 bg-charcoal-800/40 backdrop-blur-sm flex items-start justify-center p-4 pt-12 overflow-y-auto"
    onClick={onClose}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className={`bg-white rounded-3xl shadow-xl w-full ${narrow ? "max-w-md" : "max-w-2xl"} p-6 md:p-8`}
    >
      {children}
    </div>
  </div>
);
